import React, { FC } from 'react';

import { dateToDayInfo, DatePickerProps, addStartDays, subtractStartDays } from './DatePicker';
import { range } from '../../../shared/lib/functional';
import { todayDate } from '../../../shared/lib/time';

import './MonthDatePicker.scss';
import { TextButton, InlineIcon, Button } from '@spark-labs/avocado-ui';

const DAY_MILLIS = 24 * 60 * 60 * 1000;
const ONE_SEVENTH = '14.28%';
const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};
const WEEK_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

type MonthDatePickerProps = DatePickerProps;

function daysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), 0).getDate();
}

const MonthDatePicker: FC<MonthDatePickerProps> = ({
  startDate,
  selectedDate,
  onSelectedDateChange,
  onStartDateChange,
  onSailingsButtonClick,
}) => {
  let firstDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  let month = firstDayOfMonth.getMonth();

  let days = [];
  let currentDay = new Date(firstDayOfMonth);

  while (currentDay.getMonth() === month) {
    days.push(dateToDayInfo(currentDay, selectedDate));

    currentDay = new Date(currentDay.getTime() + DAY_MILLIS);
  }

  // Add empty week days at the beginning
  for (let i = 0; i < firstDayOfMonth.getDay(); i++) {
    days.unshift(undefined);
  }

  return (
    <div className="bg-blue-lighter">
      <div className="flex items-center mb-4">
        <TextButton
          className="mr-auto ml-1"
          onClick={() =>
            onStartDateChange(subtractStartDays(startDate, daysInMonth(firstDayOfMonth)))
          }
        >
          <InlineIcon className="text-white text-2xl ml-2" name="chevron-circle-left" />
        </TextButton>

        <div className="text-lg font-bold text-white">
          {firstDayOfMonth.toLocaleString('en', { month: 'long', year: 'numeric' })}
        </div>

        <TextButton
          className="ml-auto mr-1"
          onClick={() => onStartDateChange(addStartDays(startDate, daysInMonth(firstDayOfMonth)))}
        >
          <InlineIcon className="text-white text-2xl mr-2" name={'chevron-circle-right'} />
        </TextButton>
      </div>

      <div className="flex flex-wrap">
        {range(7).map(i => (
          <div
            className="text-grey-darker text-center text-xs"
            style={{ width: ONE_SEVENTH }}
            key={`week-header-${i}`}
          >
            {WEEK_DAYS[i]}
          </div>
        ))}

        {days.map((day, i) =>
          day ? (
            <div
              key={i}
              style={{
                width: ONE_SEVENTH,
              }}
              className="MonthPickerButton"
            >
              <TextButton
                className={`MonthPickerButtonContent text-white text-center items-center
rounded-full flex flex-col justify-center ${day.selected ? 'bg-green' : ''}`}
                onClick={() => onSelectedDateChange(day.date)}
              >
                <div className="text-lg">{day.dayOfMonth}</div>
              </TextButton>
            </div>
          ) : (
            <div key={i} style={{ width: ONE_SEVENTH }} />
          )
        )}
      </div>

      <div className="flex flex-col items-center text-lg">
        <Button
          className="w-3/4 bg-blue-light p-2 text-white font-bold mb-1"
          onClick={() => {
            onSelectedDateChange(todayDate());
            onStartDateChange(todayDate());
          }}
        >
          Today: {todayDate().toLocaleString('en', DATE_OPTIONS)}
        </Button>
        <Button
          className="w-3/4 bg-blue-light p-2 text-white font-bold mb-2"
          onClick={onSailingsButtonClick}
        >
          View Sailings
        </Button>
      </div>
    </div>
  );
};

export default MonthDatePicker;
